import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { createCompetition } from '@/lib/firebase/queries/competitions';
import { buildCompetitionCopy, defaultCopyDates } from '@/lib/competitions/copyCompetition';
import { newCompetitionHabitId } from '@/lib/competitions/competitionHabits';
import type { Competition } from '@/types/competitions';
import { cn } from '@/lib/utils';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export interface CopyTargetOrganization {
  id: string;
  name: string;
}

interface CopyCompetitionDialogProps {
  source: Competition;
  organizations: CopyTargetOrganization[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCopied: (organizationId: string) => void;
}

const beforeToday = (date: Date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);
  return day < today;
};

/**
 * Copy a competition, e.g. into another organization (namat#27). The copy is
 * a draft with the same habits; logs, standings and participants stay behind.
 */
export function CopyCompetitionDialog({ source, organizations, open, onOpenChange, onCopied }: CopyCompetitionDialogProps) {
  const { t, dir } = useLanguage();
  const { currentUser } = useAuth();
  const [name, setName] = useState(source.name);
  const [organizationId, setOrganizationId] = useState(source.organizationId);
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [saving, setSaving] = useState(false);
  const label = (key: string, fallback: string) => t(`dashboard.manager.competitions.copy.${key}`) || fallback;
  const align = dir === 'rtl' ? 'text-right' : 'text-left';

  useEffect(() => {
    if (!open) return;
    const dates = defaultCopyDates(source, new Date());
    setName(source.name);
    setOrganizationId(source.organizationId);
    setStartDate(dates.startDate);
    setEndDate(dates.endDate);
  }, [open, source]);

  const datesValid = !!startDate && !!endDate && endDate >= startDate;

  const handleCopy = async () => {
    if (!currentUser) {
      toast.error(t('dashboard.manager.competitions.messages.loginRequired') || 'You must be logged in to create a competition');
      return;
    }
    if (!startDate || !endDate || !datesValid) {
      toast.error(t('dashboard.manager.competitions.validation.endDateAfterStart') || 'End date must be after start date');
      return;
    }
    try {
      setSaving(true);
      await createCompetition(buildCompetitionCopy(source, {
        name,
        organizationId,
        startDate,
        endDate,
        createdBy: currentUser.uid,
        newHabitId: () => newCompetitionHabitId(),
      }));
      const target = organizations.find(o => o.id === organizationId)?.name ?? '';
      toast.success(label('copied', 'Copied as a draft to {organization}').replace('{organization}', target));
      onCopied(organizationId);
      onOpenChange(false);
    } catch (error) {
      console.error('Error copying competition:', error);
      if (error instanceof Error && error.message.includes('competition that overlaps with these dates')) {
        toast.error(t('dashboard.manager.competitions.messages.overlappingCompetitionExists') || 'Organization already has a competition that overlaps with these dates. Competitions cannot have overlapping time periods.');
      } else {
        toast.error(label('copyFailed', 'Failed to copy the competition'));
      }
    } finally {
      setSaving(false);
    }
  };

  const dateField = (id: string, text: string, value: Date | undefined, onChange: (d: Date | undefined) => void) => (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>{text}</Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button id={id} type="button" variant="outline" className={cn('w-full pl-3 text-left font-normal', !value && 'text-muted-foreground')}>
            {value ? format(value, 'PPP') : text}
            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" selected={value} onSelect={onChange} disabled={beforeToday} initialFocus />
        </PopoverContent>
      </Popover>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" dir={dir}>
        <DialogHeader>
          <DialogTitle className={align}>{label('title', 'Copy competition')}</DialogTitle>
          <DialogDescription className={align}>
            {label('description', 'Creates a draft with the same habits. Logs, standings and participants are not copied. Publish it when it is ready.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="copy-competition-name">{t('dashboard.manager.competitions.name') || 'Competition Name'}</Label>
            <Input id="copy-competition-name" value={name} onChange={e => setName(e.target.value)} />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="copy-competition-org">{label('targetOrganization', 'Organization')}</Label>
            <Select value={organizationId} onValueChange={setOrganizationId}>
              <SelectTrigger id="copy-competition-org">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {organizations.map(org => (
                  <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {dateField('copy-competition-start', t('dashboard.manager.competitions.startDate') || 'Start Date', startDate, setStartDate)}
            {dateField('copy-competition-end', t('dashboard.manager.competitions.endDate') || 'End Date', endDate, setEndDate)}
          </div>
          {!datesValid && startDate && endDate && (
            <p className="text-sm text-destructive">
              {t('dashboard.manager.competitions.validation.endDateAfterStart') || 'End date must be after start date'}
            </p>
          )}

          <p className="text-sm text-muted-foreground">
            {label('habitsSummary', '{count} habits will be copied.').replace('{count}', String(source.habits.length))}
          </p>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t('dashboard.manager.competitions.cancel') || 'Cancel'}
          </Button>
          <Button type="button" onClick={handleCopy} disabled={saving || !datesValid}>
            {saving ? label('copying', 'Copying...') : label('confirm', 'Copy as draft')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
